import { LocalizationSerializer } from "@serializers/localization"
import { ArticleSerializer } from "@serializers/article"
import { InstagramSerializer } from "@serializers/instagram"
import { BranchLinksSerializer } from "@serializers/branch-links"
import { TikTokSerializer } from "@serializers/tiktok"
import { config } from "@lib/config"

import { BaseHttpProvider } from "./base-http.provider"

import type { AxiosError } from "axios"
import type { LanguageISO } from "./types/lang"

export class BlogProvider extends BaseHttpProvider {
  async getBlogPage(locale: LanguageISO, countryCode?: string) {
    try {
      const { data, meta } = await this.execute({
        uri: "/blog-page",
        query: {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          _locale: locale,
          ...(countryCode && { country_code: countryCode }),
        },
      })

      return {
        enabledYoutube: meta.enabled_youtube,
        enabledInstagram: meta.enabled_instagram,
        templates: meta.templates.map((template) =>
          LocalizationSerializer(template),
        ),
        branchLinksJournal: BranchLinksSerializer(meta.branch_links_journal),
        promo: data.promo ? ArticleSerializer({ post: data.promo }) : null,
        editorChoice: data.editor_choice
          ? data.editor_choice.map((post) => ArticleSerializer({ post }))
          : null,
        instagrams: data.instagrams.map((instagram) =>
          InstagramSerializer({ instagram }),
        ),
        tiktoks: data.tiktoks
          ? data.tiktoks
              .map(TikTokSerializer)
              .filter((tiktok) => Boolean(tiktok.image))
          : [],
        promoBanner: meta.promo_banner,
      }
    } catch (err) {
      if ((err as AxiosError).response) {
        const error = err as AxiosError
        console.error(error.response) // eslint-disable-line no-console
      }
      throw err
    }
  }

  async getPosts(params: {
    lang: LanguageISO
    page: number
    pageSize: number
    countryCode?: string
  }) {
    const { lang = "ru", page = 1, pageSize = 7, countryCode } = params
    const response = await this.execute({
      uri: "/posts",
      query: {
        lang,
        page,
        pageSize,
        ...(countryCode && { country_code: countryCode }),
      },
    })
    // @ts-ignore
    const data = response.data.map((post) => ArticleSerializer({ post }))
    return {
      data,
      pagination: response.meta.pagination,
    }
  }

  async getPost(lang: LanguageISO, slug: string, countryCode?: string) {
    try {
      const data = await this.execute({
        uri: `/posts/${slug}/slug`,
        query: {
          lang,
          ...(countryCode && { country_code: countryCode }),
        },
      })
      return ArticleSerializer({ post: data, shorted: false })
    } catch (err) {
      throw err
    }
  }

  async getLastVideos(maxResults = 12) {
    try {
      const data = await this.execute(
        {
          uri: "/search",
          query: {
            key: config.data.youtubeApiKey,
            channelId: config.data.youtubeEnChannel,
            maxResults,
            part: "snippet",
            order: "date",
          },
        },
        "https://www.googleapis.com/youtube/v3",
      )
      // @ts-ignore
      return data.items.map((item) => ({
        id: item.id.videoId,
        image: item.snippet.thumbnails.high.url,
        title: item.snippet.title,
      }))
    } catch {}
  }

  async getPostsByCategory(
    category: string,
    lang: LanguageISO,
    countryCode?: string,
  ) {
    try {
      const { data } = await this.execute({
        uri: `/posts/categories/${category}`,
        query: {
          lang,
          ...(countryCode && { country_code: countryCode }),
        },
      })
      // @ts-ignore
      const posts = data.map((post) => ArticleSerializer({ post }))
      return {
        posts,
      }
    } catch (err) {
      throw err
    }
  }

  async getPostsBySlug(slug: string, countryCode?: string) {
    try {
      const { data } = await this.execute({
        uri: `/posts/slugs/${slug}`,
        query: {
          ...(countryCode && { country_code: countryCode }),
        },
      })
      // @ts-ignore
      const posts = data.map((post) => ArticleSerializer({ post }))
      return { posts }
    } catch (err) {
      throw err
    }
  }

  async getCategories(lang: LanguageISO, countryCode?: string) {
    try {
      const response = await this.execute({
        uri: "/categories",
        query: {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          _locale: lang,
          ...(countryCode && { country_code: countryCode }),
        },
      })
      return {
        data: response,
      }
    } catch (err) {
      throw err
    }
  }

  async getCategoriesBySlug(slug: string) {
    try {
      const res = await this.execute({
        uri: `/categories/slugs/${slug}`,
      })

      return { categories: res }
    } catch (err) {
      throw err
    }
  }

  async getRecommendedPosts(id: string | number, countryCode?: string) {
    try {
      const response = await this.execute({
        uri: "/posts/recommended",
        query: {
          postId: id,
          ...(countryCode && { country_code: countryCode }),
        },
      })
      return {
        // @ts-ignore
        data: response.data.map((post) => ArticleSerializer({ post })),
      }
    } catch (err) {
      throw err
    }
  }

  async readDefaultArticlesBanner(locale: LanguageISO) {
    return await this.execute({
      uri: `/blog-page/default-posts-banner?locale=${locale}`,
    })
  }
}
